import { createTheme } from "@mui/material/styles";
import { isTestEnv, isUatEnv } from "./common/helpers/envHelpers";

const headerFont = '"Quicksand", sans-serif';
const bodyFont = '"Source Sans Pro", sans-serif';

declare module "@mui/material/styles" {
  interface TypographyVariants {
    secondary: React.CSSProperties;
    disabled: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    secondary?: React.CSSProperties;
    disabled?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    secondary: true;
    disabled: true;
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    header: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    header?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    header: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    text: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    hover: {
      primaryLight: string;
    };
    gray: {
      primary: string;
      light: string;
      dark: string;
    };
  }

  interface PaletteOptions {
    hover?: {
      primaryLight?: string;
    };
    gray?: {
      primary: string;
      light: string;
      dark: string;
    };
  }

  interface TypeBackground {
    light: string;
    dark: string;
    darkHover: string;
  }
}

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xxl: true;
  }
}

export const theme = createTheme({
  typography: {
    fontFamily: bodyFont,
    secondary: {
      color: "rgba(0, 0, 0, 0.52)",
    },
    disabled: {
      color: "rgba(0, 0, 0, 0.38)",
    },
    header: {
      fontFamily: headerFont,
      display: "block",
    },
  },
  palette: {
    primary: {
      main: "#4A1D9C",
      light: "#895FDFE5",
      dark: "#1D095A",
    },
    success: {
      main: "#19B800",
    },
    error: {
      main: "#FF505A",
      dark: "#C50631",
    },
    warning: {
      main: "#FF9600",
    },
    info: {
      main: "#FDD835",
    },
    background: {
      default: "#F3F3F7",
      light: "#e7e7eb",
      dark: "#383838",
      darkHover: "#505050",
    },
    hover: {
      primaryLight: "#6D4AB5E5",
    },
    text: {
      primary: "#000000DE",
      secondary: "rgba(0, 0, 0, 0.52)",
      disabled: "rgba(0, 0, 0, 0.38)",
    },
    gray: {
      primary: "#c2c2c6",
      light: "#e7e7eb",
      dark: "#aaaaad",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 2000,
    },
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: isTestEnv() || isUatEnv() ? "#D7B4F5" : "#1D0A5A",
          color: "#fff",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: "#fff",
          boxShadow: "unset",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontWeight: 600,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }: any) => ({
          ...((ownerState.variant === "contained" ||
            ownerState.variant === "outlined" ||
            ownerState.variant === "text") && {
            fontFamily: headerFont,
            fontWeight: 700,
            textTransform: "none",
          }),
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          whiteSpace: "pre-line",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#F9F9FA99",
            "& fieldset": {
              borderColor: "#0008281C",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#4A1D9C !important",
              borderWidth: "2px !important",
            },
            "&.Mui-disabled fieldset": {
              borderColor: "#0008281C !important",
            },
            // TODO: check if this should be black or theme purplish color
            "&:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderColor: "#4A1D9C4D",
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: "#F9F9FA99",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0008281C",
          },
          "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
            borderColor: "#4A1D9C !important",
            borderWidth: "2px !important",
          },
          // TODO: check disabled theme for select component
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0008281C !important",
          },
          "&:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
            borderColor: "#4A1D9C4D",
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderWidth: "1px",
            borderColor: `${theme.palette.error.main} !important`,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }: any) => ({
          fontWeight: 600,
          fontSize: "14px",
          marginBottom: "5px",
          marginRight: "5px",
          ...(ownerState.variant === "filled" && {
            backgroundColor: "#895FDFE5",
            color: "#FFFFFF",
            "& .MuiChip-deleteIcon": {
              color: "#FFFFFF",
              opacity: "0.4",
            },
          }),
          ...(ownerState.variant === "text" && {
            backgroundColor: "#F3F3F7",
            color: "#4A1D9C",
          }),
        }),
      },
    },
  },
});

export default theme;
